import axios from 'axios';
import { useEffect, useState } from 'react';
import { getPaidEndpointAppCheckToken } from '../firebaseHelper';
import PreviewInfosVertical from './PreviewInfosVertical';
import SearchResultsLoading from './SearchResultsLoading';
import SearchEmptyState from './SearchEmptyState';

const HighlightSearchResults = ({ query, speakerId, podcastId, entityName, onSearchEpisodes }) => {
    const searchQuery = (query || '').trim();
    const [retry, setRetry] = useState(0);
    const [result, setResult] = useState(null);
    const requestKey = JSON.stringify([searchQuery, speakerId, podcastId, retry]);

    useEffect(() => {
        if (!searchQuery) return;

        const controller = new AbortController();
        let current = true;
        setResult({ requestKey, status: 'loading' });

        const search = async () => {
            try {
                const appCheckToken = await getPaidEndpointAppCheckToken();
                if (!current) return;
                const { data } = await axios.get('/highlight_search', {
                    params: {
                        query: searchQuery,
                        ...(speakerId ? { speakerId } : {}),
                        ...(podcastId ? { podcastId } : {}),
                        limit: 20,
                    },
                    headers: { 'X-Firebase-AppCheck': appCheckToken },
                    signal: controller.signal,
                    timeout: 12000,
                });
                if (!current) return;
                if (!Array.isArray(data?.previews)) {
                    throw new Error('Invalid highlight search response');
                }
                setResult({ requestKey, status: 'ready', previews: data.previews });
            } catch {
                if (current) setResult({ requestKey, status: 'error' });
            }
        };
        search();

        return () => {
            current = false;
            controller.abort();
        };
    }, [searchQuery, speakerId, podcastId, requestKey]);

    if (!searchQuery) return null;

    if (result?.requestKey !== requestKey || result.status === 'loading') {
        return <SearchResultsLoading kind='highlights' />;
    }

    if (result.status === 'error') {
        return (
            <div role='alert'>
                <SearchEmptyState message="Couldn't load highlights. Please try again." />
                <div style={{ textAlign: 'center', paddingBottom: '20px' }}>
                    <button type='button' className='clampSafeButton' onClick={() => setRetry(retry + 1)}>
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (result.previews.length === 0) {
        const scopeDescription = entityName ? ` for ${entityName}` : '';
        return <>
            <SearchEmptyState message={`No highlights matching "${searchQuery}"${scopeDescription}.`} />
            {onSearchEpisodes && <div style={{ textAlign: 'center', paddingBottom: '20px' }}>
                <button type='button' className='clampSafeButton' onClick={onSearchEpisodes}>
                    Search episodes
                </button>
            </div>}
        </>;
    }

    return (
        <div className='searchResults'>
            <div style={{ padding: '16px 0px' }}>
                <PreviewInfosVertical previews={result.previews} playbackOrigin={{ kind: 'search' }} />
            </div>
        </div>
    );
};

export default HighlightSearchResults;
